import { OAuthApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  Service,
  ServiceConfiguration,
  Shard,
  RestartServiceRequest,
  RestartServiceResponse,
  RestartMultiRequest,
  RestartMultiResponse,
  ScaleServiceRequest,
  ScaleServiceResponse,
  BulkOperationRequest,
  BulkOperationResponse,
  TierFilter,
  UpdateScalerRequest,
  UpdateScalerResponse,
  LogSamplingState,
  SetLogSamplingRuleRequest,
  SetLogSamplingRuleResponse,
  PodCountResult,
  DatabaseHostRef,
  DatabaseHostsResponse,
  WhoAmIResponse,
  CapabilitiesResponse,
  PreflightResult,
  HistoryResponse,
  ShardLedger,
} from './types';

export interface OpsicleApi {
  getServices(teamName?: string, requestedUser?: string): Promise<Service[]>;
  getShardsByService(serviceName?: string): Promise<Shard[]>;
  getAllShards(): Promise<Shard[]>;
  getServiceConfiguration(
    serviceName: string,
    shardName: string,
  ): Promise<ServiceConfiguration>;
  getPodCount(serviceName: string, shardName: string): Promise<PodCountResult>;
  checkTeamAuthorization(teamName: string): Promise<boolean>;
  checkServiceAuthorization(serviceOwner: string): Promise<boolean>;
  restartService(
    request: RestartServiceRequest,
  ): Promise<RestartServiceResponse>;
  restartServiceOnMultiple(
    request: RestartMultiRequest,
  ): Promise<RestartMultiResponse>;
  scaleService(request: ScaleServiceRequest): Promise<ScaleServiceResponse>;
  updateScalerParameters(
    request: UpdateScalerRequest,
  ): Promise<UpdateScalerResponse>;
  getServicesByTier(tierFilter: TierFilter): Promise<Service[]>;
  bulkShutdownServices(
    request: BulkOperationRequest,
  ): Promise<BulkOperationResponse>;
  bulkRestartServices(
    request: BulkOperationRequest,
  ): Promise<BulkOperationResponse>;
  getLogSamplingPilotShards(serviceName: string): Promise<Shard[]>;
  getLogSamplingState(
    serviceName: string,
    clickupShard: string,
  ): Promise<LogSamplingState>;
  setLogSamplingRule(
    request: SetLogSamplingRuleRequest,
  ): Promise<SetLogSamplingRuleResponse>;
  listDataPlatformShards(): Promise<string[]>;
  getDatabaseHosts(clickupShard: string): Promise<DatabaseHostsResponse>;
  blacklistDatabaseHost(
    clickupShard: string,
    host: DatabaseHostRef,
    reason: string,
  ): Promise<void>;
  unblacklistDatabaseHost(
    clickupShard: string,
    host: DatabaseHostRef,
  ): Promise<void>;
  whoami(): Promise<WhoAmIResponse>;
  capabilities(): Promise<CapabilitiesResponse>;
  preflight(serviceName: string): Promise<PreflightResult>;
  readLedger(correlationId: string): Promise<ShardLedger>;
  getHistory(options?: {
    service?: string;
    capability?: string;
    envTier?: string;
    decision?: 'allow' | 'deny';
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<HistoryResponse>;
}

export class OpsicleApiClient implements OpsicleApi {
  private readonly discoveryApi: {
    getBaseUrl(pluginId: string): Promise<string>;
  };
  private readonly fetchApi: { fetch: typeof fetch };
  private readonly oktaAuthApi: Pick<OAuthApi, 'getAccessToken'>;

  constructor(options: {
    discoveryApi: { getBaseUrl(pluginId: string): Promise<string> };
    fetchApi?: { fetch: typeof fetch };
    oktaAuthApi: Pick<OAuthApi, 'getAccessToken'>;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: fetch.bind(window) };
    this.oktaAuthApi = options.oktaAuthApi;
  }

  async getServices(
    teamName?: string,
    requestedUser?: string,
  ): Promise<Service[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/services`);
    if (teamName) {
      url.searchParams.set('team', teamName);
    }
    if (requestedUser) {
      url.searchParams.set('user', requestedUser);
    }
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getShardsByService(serviceName?: string): Promise<Shard[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/shards`);
    if (serviceName) {
      url.searchParams.set('serviceName', serviceName);
    }
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getServiceConfiguration(
    serviceName: string,
    shardName: string,
  ): Promise<ServiceConfiguration> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/serviceConfig`);
    url.searchParams.set('serviceName', serviceName);
    url.searchParams.set('shardName', shardName);
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getPodCount(
    serviceName: string,
    shardName: string,
  ): Promise<PodCountResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/podCount`);
    url.searchParams.set('serviceName', serviceName);
    url.searchParams.set('shardName', shardName);
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async checkTeamAuthorization(teamName: string): Promise<boolean> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/authorize/${encodeURIComponent(teamName)}`,
    );

    if (!response.ok) {
      // If the request fails, assume not authorized
      return false;
    }

    const result = await response.json();
    return result.authorized === true;
  }

  async checkServiceAuthorization(serviceOwner: string): Promise<boolean> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/authorize/service/${encodeURIComponent(serviceOwner)}`,
    );

    if (!response.ok) {
      // If the request fails, assume not authorized
      return false;
    }

    const result = await response.json();
    return result.authorized === true;
  }

  async restartService(
    request: RestartServiceRequest,
  ): Promise<RestartServiceResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/restart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async restartServiceOnMultiple(
    request: RestartMultiRequest,
  ): Promise<RestartMultiResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/restart/multi`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    // The backend returns 400 (not just non-2xx failures) when every
    // requested pair is dropped — that response body is still meaningful
    // (dispatchedPairs/droppedPairs), not just an error to throw away. Only
    // treat it as a thrown error if the body isn't the expected shape.
    if (!response.ok) {
      const cloned = response.clone();
      try {
        const body = await cloned.json();
        if (typeof body?.success === 'boolean') {
          return body;
        }
      } catch {
        // fall through to the generic error path below
      }
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async scaleService(
    request: ScaleServiceRequest,
  ): Promise<ScaleServiceResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/scale`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async updateScalerParameters(
    request: UpdateScalerRequest,
  ): Promise<UpdateScalerResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/scaler`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getServicesByTier(tierFilter: TierFilter): Promise<Service[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/services/tier`);
    url.searchParams.set('filter', tierFilter);

    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getAllShards(): Promise<Shard[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/shards/all`);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async bulkShutdownServices(
    request: BulkOperationRequest,
  ): Promise<BulkOperationResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/bulk/shutdown`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async bulkRestartServices(
    request: BulkOperationRequest,
  ): Promise<BulkOperationResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/bulk/restart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getLogSamplingPilotShards(serviceName: string): Promise<Shard[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/log_sampling/pilot_shards`);
    url.searchParams.set('serviceName', serviceName);
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getLogSamplingState(
    serviceName: string,
    clickupShard: string,
  ): Promise<LogSamplingState> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/log_sampling`);
    url.searchParams.set('serviceName', serviceName);
    url.searchParams.set('clickupShard', clickupShard);
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async setLogSamplingRule(
    request: SetLogSamplingRuleRequest,
  ): Promise<SetLogSamplingRuleResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/log_sampling`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async listDataPlatformShards(): Promise<string[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/data-platform/shards`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getDatabaseHosts(clickupShard: string): Promise<DatabaseHostsResponse> {
    const oktaToken = await this.dataPlatformOktaToken();
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/data-platform/database-hosts`);
    url.searchParams.set('clickupShard', clickupShard);
    const response = await this.fetchApi.fetch(url.toString(), {
      headers: { 'x-okta-token': oktaToken },
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async whoami(): Promise<WhoAmIResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/whoami`);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async capabilities(): Promise<CapabilitiesResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(`${baseUrl}/capabilities`);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async preflight(serviceName: string): Promise<PreflightResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/preflight`);
    url.searchParams.set('service', serviceName);
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async readLedger(correlationId: string): Promise<ShardLedger> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/ledger/${encodeURIComponent(correlationId)}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getHistory(options?: {
    service?: string;
    capability?: string;
    envTier?: string;
    decision?: 'allow' | 'deny';
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<HistoryResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const url = new URL(`${baseUrl}/history`);
    if (options?.service) {
      url.searchParams.set('service', options.service);
    }
    if (options?.capability) {
      url.searchParams.set('capability', options.capability);
    }
    if (options?.envTier) {
      url.searchParams.set('envTier', options.envTier);
    }
    if (options?.decision) {
      url.searchParams.set('decision', options.decision);
    }
    if (options?.search) {
      url.searchParams.set('search', options.search);
    }
    if (options?.limit !== undefined) {
      url.searchParams.set('limit', String(options.limit));
    }
    if (options?.offset !== undefined) {
      url.searchParams.set('offset', String(options.offset));
    }
    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async blacklistDatabaseHost(
    clickupShard: string,
    host: DatabaseHostRef,
    reason: string,
  ): Promise<void> {
    await this.sendDatabaseHostBlacklist('POST', clickupShard, host, reason);
  }

  async unblacklistDatabaseHost(
    clickupShard: string,
    host: DatabaseHostRef,
  ): Promise<void> {
    await this.sendDatabaseHostBlacklist('DELETE', clickupShard, host);
  }

  private async sendDatabaseHostBlacklist(
    method: 'POST' | 'DELETE',
    clickupShard: string,
    { group, type, prrNumber }: DatabaseHostRef,
    reason?: string,
  ): Promise<void> {
    const oktaToken = await this.dataPlatformOktaToken();
    const baseUrl = await this.discoveryApi.getBaseUrl('opsicle');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/data-platform/database-hosts/blacklist`,
      {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-okta-token': oktaToken,
        },
        body: JSON.stringify({ clickupShard, group, type, prrNumber, reason }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  private dataPlatformOktaToken(): Promise<string> {
    return this.oktaAuthApi.getAccessToken(['groups', 'email']);
  }
}
